import React from 'react';
import { Input, Radio, Select, Tooltip } from 'antd';
import styled from 'styled-components';

import {
  ASSET_PRESETS,
  ASSET_TYPES,
  AssetType,
  DEFAULT_ASSET_TYPE,
  DEFAULT_THEME_MODE,
  IBrandTokens,
  IOperatorBranding,
  PLATFORM_DEFAULTS,
  THEME_MODES,
  THEME_MODE_LABELS,
  ThemeMode,
} from '../../../types/models/tenancy';

const Wrapper = styled.div`
  .section {
    margin-bottom: 22px;
  }

  .section-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--ancana-font-light);
    margin-bottom: 8px;
  }

  .hint {
    font-size: 12px;
    color: var(--ancana-font-light);
    margin: 4px 0 0;
  }

  .field {
    margin-bottom: 12px;
  }

  .field-label {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .colors {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .color-field {
    flex: 1;
    min-width: 190px;
  }

  .color-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* The native picker: antd 4 has no ColorPicker, and a swatch that opens the
     OS picker is what an operator expects from a color field anyway. */
  input[type='color'] {
    width: 38px;
    height: 32px;
    padding: 2px;
    border: 1px solid var(--ancana-lightergray);
    border-radius: 2px;
    background: none;
    cursor: pointer;
    flex: none;
  }

  .sample {
    background: var(--ancana-lightestgray);
    border: 1px solid var(--ancana-lightergray);
    border-radius: 2px;
    padding: 6px 10px;
    font-size: 13px;
    color: var(--ancana-font-light);
    margin-top: 8px;
  }

  .mode-options {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .mode-hint {
    font-size: 12px;
    color: var(--ancana-font-light);
    margin-left: 24px;
  }
`;

const HEX = /^#[0-9a-fA-F]{6}$/;

interface ColorFieldProps {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
}

const ColorField = ({ label, hint, value, onChange }: ColorFieldProps) => (
  <div className="color-field">
    <div className="field-label">{label}</div>
    <div className="color-row">
      {/* The native input only accepts `#rrggbb`; feed it the default rather
          than a half-typed value, or it silently snaps back to black. */}
      <input
        type="color"
        value={HEX.test(value) ? value : PLATFORM_DEFAULTS.primary}
        onChange={e => onChange(e.target.value)}
      />
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={PLATFORM_DEFAULTS.primary}
        maxLength={7}
        style={{ fontFamily: 'monospace' }}
      />
    </div>
    <p className="hint">{hint}</p>
  </div>
);

export interface BrandingFieldsProps {
  value: IOperatorBranding;
  onChange: (branding: IOperatorBranding) => void;
}

/**
 * The editable half of a tenant's presentation config — the same choices Caim's
 * own `BrandingEditor` offers an operator about themselves, here on the
 * platform side so staff can configure a tenant during onboarding, before that
 * tenant has anyone to log in and do it.
 *
 * Two colors and a mode, not a full palette. Everything else Caim renders —
 * background, text, muted text, borders, the text color on the primary fill —
 * is derived from the surface HUE by `lib/theme.ts` so the portal is WCAG AA by
 * construction. Offering those here would let staff ship a tenant a portal that
 * fails contrast, and the values would be ignored anyway.
 *
 * The derived palette is deliberately not previewed. Reproducing it means a
 * second copy of Caim's OKLab derivation engine, and a preview that drifts from
 * the renderer is worse than none: it would show staff a portal no owner sees.
 * The two chosen colors are shown as swatches — those are the actual inputs.
 */
const BrandingFields = ({ value, onChange }: BrandingFieldsProps) => {
  const tokens = value.theme_tokens || {};
  const colors = tokens.colors || {};
  const assetType = tokens.vocabulary?.assetType || DEFAULT_ASSET_TYPE;
  const mode = (THEME_MODES as readonly string[]).includes(tokens.mode as string)
    ? (tokens.mode as ThemeMode)
    : DEFAULT_THEME_MODE;
  const preset = ASSET_PRESETS[assetType];

  const setTokens = (patch: Partial<IBrandTokens>) =>
    onChange({ ...value, theme_tokens: { ...tokens, ...patch } });

  const setColor = (key: 'primary' | 'surface', color: string) =>
    setTokens({ colors: { ...colors, [key]: color } });

  return (
    <Wrapper>
      <div className="section">
        <div className="section-label">What this tenant manages</div>
        <div className="field" style={{ maxWidth: 260 }}>
          <Select<AssetType>
            value={assetType}
            onChange={next => setTokens({
              // Written whole: `theme_tokens` is replaced rather than merged on
              // save, so a partially-written vocabulary would drop the tenant's
              // noun override and move their portal back to "home".
              vocabulary: { ...(tokens.vocabulary || {}), assetType: next },
            })}
            style={{ width: '100%' }}
          >
            {ASSET_TYPES.map(type => (
              <Select.Option key={type} value={type}>
                {ASSET_PRESETS[type].icon} {ASSET_PRESETS[type].label}
              </Select.Option>
            ))}
          </Select>
        </div>
        <p className="hint">
          Sets the noun this operator&apos;s owners and staff see everywhere, in both
          languages, plus the field labels that go with it. It moves no data.
        </p>
        <div className="sample">
          “Add {preset.sample}” · “{preset.label}” · “{preset.numberLabel}”
        </div>
      </div>

      <div className="section">
        <div className="section-label">Brand colors</div>
        <div className="colors">
          <ColorField
            label="Primary"
            hint="Buttons, links, active states — the main identity color."
            value={colors.primary || ''}
            onChange={color => setColor('primary', color)}
          />
          <ColorField
            label="Surface"
            hint="Read as a hue: tints the whole portal — background, borders and text."
            value={colors.surface || ''}
            onChange={color => setColor('surface', color)}
          />
        </div>
        <p className="hint">
          Left empty, each falls back to the platform default at render time. The rest
          of the palette is derived from these two so the portal stays legible — those
          tokens aren&apos;t editable here or in the operator&apos;s own console.
        </p>
      </div>

      <div className="section">
        <div className="section-label">Theme</div>
        <Radio.Group
          value={mode}
          onChange={e => setTokens({ mode: e.target.value })}
          className="mode-options"
        >
          {THEME_MODES.map(option => (
            <div key={option}>
              <Radio value={option}>{THEME_MODE_LABELS[option].label}</Radio>
              <div className="mode-hint">{THEME_MODE_LABELS[option].hint}</div>
            </div>
          ))}
        </Radio.Group>
      </div>

      <div className="section">
        <div className="section-label">Shape &amp; assets</div>
        <div className="field" style={{ maxWidth: 200 }}>
          <div className="field-label">
            <Tooltip title="Any CSS length — 0 for square corners, 0.625rem for the platform default.">
              Corner radius
            </Tooltip>
          </div>
          <Input
            value={tokens.radius ?? ''}
            onChange={e => setTokens({ radius: e.target.value })}
            placeholder={PLATFORM_DEFAULTS.radius}
          />
        </div>
        <div className="field">
          <div className="field-label">Logo URL</div>
          <Input
            value={value.logo_url}
            onChange={e => onChange({ ...value, logo_url: e.target.value })}
            placeholder="https://…/logo.svg"
          />
        </div>
        <div className="field">
          <div className="field-label">Dark-theme logo URL</div>
          <Input
            value={value.logo_dark_url}
            onChange={e => onChange({ ...value, logo_dark_url: e.target.value })}
            placeholder="Falls back to the logo above"
          />
        </div>
        <div className="field">
          <div className="field-label">Favicon URL</div>
          <Input
            value={value.favicon_url}
            onChange={e => onChange({ ...value, favicon_url: e.target.value })}
            placeholder="https://…/favicon.png"
          />
        </div>
        <p className="hint">
          Assets are URLs, not uploads — they&apos;re pushed to S3 by the operator-config
          tooling and referenced here.
        </p>
      </div>
    </Wrapper>
  );
};

export default BrandingFields;
